import { useState, useEffect } from "react";
import AdminSidebar from "./components/AdminSidebar";
import AdminHero from "./components/AdminHero";
import AdminStats from "./components/AdminStats";
import AdminProducts from "./components/AdminProducts";
import AdminProperties from "./components/AdminProperties";
import AdminTestimonials from "./components/AdminTestimonials";
import AdminTeam from "./components/AdminTeam";
import AdminContact from "./components/AdminContact";
import AdminBrochure from "./components/AdminBrochure";
import AdminNavbar from "./components/AdminNavbar";
import AdminFooter from "./components/AdminFooter";
import AdminSocialHousing from "./components/AdminSocialHousing";
import AdminLogin from "./components/AdminLogin";

export type AdminSection =
  | "hero"
  | "stats"
  | "products"
  | "properties"
  | "testimonials"
  | "team"
  | "contact"
  | "brochure"
  | "navbar"
  | "footer"
  | "social-housing";

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeSection, setActiveSection] = useState<AdminSection>("hero");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const auth = sessionStorage.getItem("td_admin_auth");
    if (auth === "1") setIsAuthenticated(true);
  }, []);

  const handleLogin = () => setIsAuthenticated(true);

  const handleLogout = () => {
    sessionStorage.removeItem("td_admin_auth");
    setIsAuthenticated(false);
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  const renderSection = () => {
    switch (activeSection) {
      case "hero": return <AdminHero onSave={handleSave} />;
      case "stats": return <AdminStats onSave={handleSave} />;
      case "products": return <AdminProducts onSave={handleSave} />;
      case "properties": return <AdminProperties onSave={handleSave} />;
      case "testimonials": return <AdminTestimonials onSave={handleSave} />;
      case "team": return <AdminTeam onSave={handleSave} />;
      case "contact": return <AdminContact onSave={handleSave} />;
      case "brochure": return <AdminBrochure onSave={handleSave} />;
      case "navbar": return <AdminNavbar onSave={handleSave} />;
      case "footer": return <AdminFooter onSave={handleSave} />;
      case "social-housing": return <AdminSocialHousing onSave={handleSave} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Bar */}
      <header
        className="bg-[#071A2E] border-b border-white/10 flex-shrink-0 z-50 sticky top-0"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="h-14 flex items-center justify-between px-4 md:px-6">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Toggle sidebar"
            >
              <i className="ri-menu-3-line text-lg leading-none" />
            </button>
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#C9A84C]/20 border border-[#C9A84C]/30">
                <i className="ri-home-4-fill text-[#C9A84C] text-sm leading-none" />
              </div>
              <div>
                <span
                  className="text-white font-bold text-sm tracking-wide"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Thorpe Dellow
                </span>
                <span className="text-[#C9A84C] text-[10px] ml-2 font-semibold uppercase tracking-widest hidden sm:inline">
                  Admin
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            {saved && (
              <span className="flex items-center gap-1.5 text-green-400 text-xs font-semibold bg-green-400/10 border border-green-400/20 px-3 py-1.5 rounded-full">
                <i className="ri-check-double-line leading-none" />
                <span className="hidden sm:inline">Saved</span>
              </span>
            )}
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/15 text-white/70 text-xs font-semibold hover:bg-white/10 hover:text-white transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-external-link-line leading-none" />
              <span className="hidden sm:inline">View Site</span>
            </a>
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#C9A84C]/20 border border-[#C9A84C]/30 text-[#C9A84C] text-xs font-bold flex-shrink-0">
                A
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-white/50 text-xs hover:text-white/80 hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap"
                title="Sign out"
              >
                <i className="ri-logout-box-line leading-none" />
                <span className="hidden sm:inline">Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <AdminSidebar
          activeSection={activeSection}
          onSelect={(s) => { setActiveSection(s); setSidebarOpen(false); }}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-slate-50">
          <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 pb-16">
            {renderSection()}
          </div>
        </main>
      </div>
    </div>
  );
}
